import { useState } from 'react';
import { all } from '@/api/northlight/policy-file-ingestion';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/custom/data-table';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { useExportCsv } from '@/hooks/use-export-csv';
import { keepPreviousData, Updater, useQuery } from '@tanstack/react-query';
import {
	ColumnFiltersState,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
	VisibilityState,
} from '@tanstack/react-table';

import { columns } from './columns';

export function PolicyFileIngestionTable() {
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10, //default
	});
	const [sorting, setSorting] = useState<SortingState>([
		{
			id: 'importDate',
			desc: true,
		},
	]);
	const { data, isFetching } = useQuery({
		queryFn: async () => all(pagination, sorting),
		queryKey: ['policy-file-ingestion', pagination, sorting],
		staleTime: 30000,
		placeholderData: keepPreviousData,
	});

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
	const [rowSelection, setRowSelection] = useState({});
	const [globalFilter, setGlobalFilter] = useState<any>([]);
	const handlePageChange = (
		updater: Updater<PaginationState, PaginationState>,
	) => {
		setPagination((prev) => {
			const newPagination =
				typeof updater === 'function' ? updater(prev) : updater;

			return newPagination; // Allow page change
		});
	};
	const table = useReactTable({
		data: data?.data ?? [],
		columns: columns,
		rowCount: data?.total ?? 0,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: setColumnFilters,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onRowSelectionChange: setRowSelection,
		onGlobalFilterChange: setGlobalFilter,
		onPaginationChange: handlePageChange,
		state: {
			sorting,
			columnFilters,
			columnVisibility,
			rowSelection,
			globalFilter,
			pagination,
		},
	});

	const { exportCsv, isExporting } = useExportCsv();

	return (
		<>
			<div className="mb-4 flex justify-between">
				<Button
					disabled={isExporting}
					onClick={() => {
						exportCsv('/NorthLightPolicy/Export', {});
					}}
					variant="outline"
				>
					Export
				</Button>
			</div>
			<div className="h-1/2 w-full">
				<DataTable
					table={table}
					isFetching={isFetching}
					noResultsMessage="No import types found."
				/>

				<TablePagination table={table} isFetching={isFetching} />
			</div>
		</>
	);
}
